// "Give me ideas" screen: curated destination ideas (data.js) plus the user's
// custom ideas, with category filters, select/deselect, a time-budget header
// (vs dayCount * 10h), and the IdeaBar for adding custom ideas / pasted
// links. Continue calls onContinue → App.finishTripCreation(selectedIdeas).
import React from 'react';
import { PALETTE, headerPad } from '../theme';
import { Icon, IconBtn, Chip, BigButton } from '../components/UI';
import { IDEA_CATS, SWATCH_BY_CAT } from '../data';
import { IdeaBar } from '../components/IdeaBar';
import { bookingInfoForIdea } from '../utils/bookingLinks';


export function BrainstormScreen({ isMobile, destination, onBack, onContinue, accent = PALETTE.accent, selectedIdeas, setSelectedIdeas, mechanic = 'grid', destIdeas = [], customIdeas = [], onAddCustom }) {
  const [cat, setCat] = React.useState('All');

  const allIdeas = [...customIdeas, ...destIdeas];
  const filtered = cat === 'All' ? allIdeas : allIdeas.filter(i => i.cat === cat);
  const selectedSet = new Set(selectedIdeas);
  const totalHours = allIdeas.filter(i => selectedSet.has(i.id)).reduce((s, i) => s + i.dur, 0);
  const tripCapHours = 50;

  const toggle = (id) => {
    setSelectedIdeas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const r = 20, circ = 2 * Math.PI * r;
  const fill = Math.min(totalHours / tripCapHours, 1) * circ;

  return (
    <div style={{ background: PALETTE.paper, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 4, background: PALETTE.paper,
        padding: headerPad(isMobile), borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 36 }}>
          <IconBtn name="arrowL" label="Back" onClick={onBack} />
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase' }}>
            {destination ? destination.split(',')[0] : 'New trip'} · Ideas
          </div>
        </div>
      </div>

      {/* Counter */}
      <div style={{
        margin: '6px 20px 16px', padding: '12px 14px',
        background: PALETTE.card, borderRadius: 12, border: `0.5px solid ${PALETTE.ruleSoft}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase' }}>
            Selected · {selectedIdeas.length} ideas
          </div>
          <div style={{ fontFamily: 'Fraunces', fontSize: 18, color: PALETTE.ink, marginTop: 2 }}>
            {totalHours.toFixed(1)}h{' '}
            <span style={{ color: PALETTE.inkMute, fontSize: 14 }}>of ~{tripCapHours}h available</span>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button
              onClick={() => setSelectedIdeas(destIdeas.map(i => i.id))}
              style={{
                appearance: 'none', border: `1px solid ${PALETTE.rule}`, cursor: 'pointer',
                padding: '4px 10px', borderRadius: 6, background: 'transparent',
                fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: PALETTE.inkSoft,
              }}
            >Select all</button>
            <button
              onClick={() => setSelectedIdeas([])}
              style={{
                appearance: 'none', border: `1px solid ${PALETTE.rule}`, cursor: 'pointer',
                padding: '4px 10px', borderRadius: 6, background: 'transparent',
                fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: PALETTE.inkSoft,
              }}
            >Deselect all</button>
          </div>
        </div>
        <div style={{ width: 48, height: 48, position: 'relative', flexShrink: 0 }}>
          <svg width="48" height="48" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r={r} fill="none" stroke={PALETTE.rule} strokeWidth="2" />
            <circle cx="24" cy="24" r={r} fill="none"
              stroke={totalHours > tripCapHours ? PALETTE.red : accent}
              strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${fill} ${circ}`} transform="rotate(-90 24 24)" />
          </svg>
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.ink, fontWeight: 600,
          }}>{Math.round(totalHours / tripCapHours * 100)}%</div>
        </div>
      </div>

      {/* IdeaBar */}
      <div style={{ padding: '0 20px 14px' }}>
        <IdeaBar onAdd={onAddCustom} accent={accent} />
      </div>

      {/* Category chips */}
      <div style={{ padding: '0 20px 14px' }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {IDEA_CATS.map(c => (
            <Chip key={c} active={cat === c} onClick={() => setCat(c)}>{c}</Chip>
          ))}
        </div>
      </div>

      {/* Ideas */}
      <div style={{ flex: 1, padding: '0 20px 100px' }}>
        <ListIdeas ideas={filtered} selectedSet={selectedSet} toggle={toggle} accent={accent} destination={destination} />
      </div>

      {/* CTA */}
      <div style={{
        position: 'sticky', bottom: 0,
        background: 'linear-gradient(to top, rgba(244,239,230,1) 60%, rgba(244,239,230,0))',
        padding: '20px 20px 28px',
      }}>
        <BigButton onClick={onContinue} style={{ background: accent, color: '#fff' }}>
          Build my itinerary <Icon name="arrow" size={18} />
        </BigButton>
      </div>
    </div>
  );
}


function IdeaDetails({ idea, destination, compact = false, inverted = false }) {
  const bookingInfo = bookingInfoForIdea(idea, destination);
  const description = bookingInfo?.description || idea.description;
  const links = bookingInfo?.links || [];
  const textColor = inverted ? 'rgba(255,255,255,0.78)' : PALETTE.inkSoft;
  const linkStyle = {
    fontFamily: 'JetBrains Mono', fontSize: compact ? 9 : 10, letterSpacing: 0.4,
    color: inverted ? '#fff' : PALETTE.accentDk, textDecoration: 'none',
    border: `1px solid ${inverted ? 'rgba(255,255,255,0.35)' : PALETTE.rule}`,
    borderRadius: 999, padding: compact ? '3px 7px' : '4px 8px',
    background: inverted ? 'rgba(255,255,255,0.12)' : PALETTE.paperDim,
  };

  if (!description && links.length === 0) return null;
  return (
    <div style={{ marginTop: compact ? 6 : 10 }}>
      {description && (
        <div style={{ fontFamily: 'Inter', fontSize: compact ? 11 : 13, color: textColor, lineHeight: 1.35 }}>
          {description}
        </div>
      )}
      {links.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: compact ? 6 : 8 }}>
          {links.map(link => (
            <a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={linkStyle}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function ListIdeas({ ideas, selectedSet, toggle, accent, destination }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {ideas.map(it => {
        const sel = selectedSet.has(it.id);
        return (
          <div
            key={it.id}
            role="button"
            tabIndex={0}
            onClick={() => toggle(it.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(it.id); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 0', cursor: 'pointer',
              borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
            }}
          >
            {/* Photo thumbnail */}
            <div style={{
              width: 76, height: 66, borderRadius: 10, flexShrink: 0,
              background: SWATCH_BY_CAT[it.cat] || PALETTE.paperDim,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 9px)',
              }} />
              {it.area && (
                <div style={{
                  position: 'absolute', bottom: 5, left: 6, right: 6,
                  fontFamily: 'JetBrains Mono', fontSize: 7,
                  color: 'rgba(255,255,255,0.9)', letterSpacing: 0.6,
                  textTransform: 'uppercase', lineHeight: 1,
                  overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                }}>
                  {it.area}
                </div>
              )}
              {sel && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0,0,0,0.28)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: 999,
                    background: accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon name="check" size={14} stroke={2.5} style={{ color: '#fff' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'Fraunces', fontSize: 16, color: PALETTE.ink,
                lineHeight: 1.25, letterSpacing: -0.2,
              }}>{it.title}</div>
              <div style={{
                fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute,
                letterSpacing: 0.5, marginTop: 4, textTransform: 'uppercase',
              }}>
                {it.cat} · {it.dur < 1 ? `${Math.round(it.dur * 60)}m` : `${it.dur}h`}
              </div>
              <IdeaDetails idea={it} destination={destination} compact />
            </div>

            {/* Checkbox */}
            <div style={{
              width: 24, height: 24, borderRadius: 999, flexShrink: 0,
              border: sel ? 'none' : `1.5px solid ${PALETTE.rule}`,
              background: sel ? accent : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {sel && <Icon name="check" size={13} stroke={2.5} style={{ color: '#fff' }} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}
